// PrintModal.js - New component for print/PDF generation
// This component handles printing in horizontal orientation with supplier checkboxes

import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './PrintModal.module.css';

// Logo import
import logo from '../../../assets/grupo_logo.png';

const PrintModal = ({ request, onClose, suppliers }) => {
    const printRef = useRef(null);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isReady, setIsReady] = useState(false);

    // Format currency
    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'R$ 0,00';
        return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    };

    // Get status label
    const getStatusLabel = (status) => {
        const labels = {
            draft: 'Rascunho',
            pending: 'Pendente',
            under_review: 'Em Análise',
            quoting: 'Em Cotação',
            quoted: 'Cotado',
            approved: 'Aprovado',
            converted: 'Convertido',
            rejected: 'Rejeitado',
            cancelled: 'Cancelado',
            partially_converted: 'Parcialmente Convertido'
        };
        return labels[status] || status;
    };

    // Get supplier name by ID
    const getSupplierName = (supplierId) => {
        const supplier = suppliers?.find(s => s.id === supplierId);
        return supplier?.razaoSocial || supplier?.name || supplierId;
    };

    // Get supplier code by ID
    const getSupplierCode = (supplierId) => {
        const supplier = suppliers?.find(s => s.id === supplierId);
        return supplier?.codigo || supplier?.code || '';
    };

    // Get all unique suppliers from all items
    const getAllSuppliers = () => {
        if (!request?.items) return [];
        const supplierMap = new Map();
        request.items.forEach(item => {
            (item.suppliers || []).forEach(s => {
                const supplier = suppliers?.find(sup => sup.id === s.supplierId);
                if (supplier && !supplierMap.has(supplier.id)) {
                    supplierMap.set(supplier.id, {
                        id: supplier.id,
                        razaoSocial: supplier.razaoSocial,
                        codigo: supplier.codigo,
                    });
                }
            });
        });
        return Array.from(supplierMap.values());
    };

    const allSuppliers = getAllSuppliers();

    // Check if a supplier quoted a specific item
    const isSupplierQuotedForItem = (supplierId, itemId) => {
        return (request?.supplierQuotes || []).some(q =>
            q.supplierId === supplierId &&
            q.items?.some(i => i.itemId === itemId) &&
            q.status !== 'rejected'
        );
    };

    // Get quote price for item from supplier
    const getQuotePriceForItem = (supplierId, itemId) => {
        const quote = (request?.supplierQuotes || []).find(q => q.supplierId === supplierId);
        if (quote) {
            const item = quote.items?.find(i => i.itemId === itemId);
            return item?.unitPrice || null;
        }
        return null;
    };

    // Get quote status for supplier
    const getSupplierQuoteStatus = (supplierId) => {
        const quote = (request?.supplierQuotes || []).find(q => q.supplierId === supplierId);
        return quote?.status || null;
    };

    // Handle print - using iframe approach for reliability
    const handlePrint = () => {
        if (!printRef.current) return;

        setIsPrinting(true);

        // Get the print content
        const printContent = printRef.current;
        const contentHTML = printContent.innerHTML;

        // Create a new window for printing
        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        if (!printWindow) {
            alert('Por favor, permita pop-ups para imprimir.');
            setIsPrinting(false);
            return;
        }

        // Write the print content
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Imprimir Solicitação - ${request?.code || ''}</title>
                    <style>
                        /* Reset and base styles */
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        body {
                            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                            background: white;
                            padding: 20px;
                            color: #1a1a2e;
                        }
                        /* Print content styles */
                        .print-content {
                            max-width: 100%;
                        }
                        .print-header {
                            text-align: center;
                            padding-bottom: 20px;
                            border-bottom: 2px solid #1a1a2e;
                            margin-bottom: 20px;
                        }
                        .print-header .logo-container {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 20px;
                            margin-bottom: 15px;
                        }
                        .print-header .logo-container img {
                            height: 70px;
                            width: auto;
                            object-fit: contain;
                        }
                        .print-header h1 {
                            font-size: 22px;
                            color: #1a1a2e;
                            letter-spacing: 1px;
                            margin: 0;
                        }
                        .print-header .subtitle {
                            font-size: 14px;
                            color: #666;
                            margin: 4px 0 0 0;
                        }
                        .print-header-info {
                            display: flex;
                            justify-content: center;
                            gap: 30px;
                            flex-wrap: wrap;
                            font-size: 13px;
                            color: #555;
                            margin-top: 10px;
                        }
                        .print-header-info div {
                            display: flex;
                            gap: 4px;
                        }
                        .print-header-info strong {
                            color: #1a1a2e;
                        }
                        .print-section {
                            margin-bottom: 20px;
                            border: 1px solid #e0e0e0;
                            border-radius: 4px;
                            overflow: hidden;
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }
                        .print-section-title {
                            background: #f5f5f5;
                            padding: 8px 15px;
                            font-weight: 600;
                            font-size: 14px;
                            border-bottom: 1px solid #e0e0e0;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                        }
                        .print-info-grid {
                            display: grid;
                            grid-template-columns: 1fr 1fr 1fr;
                            gap: 8px 20px;
                            padding: 12px 15px;
                        }
                        .print-info-grid div {
                            font-size: 13px;
                            color: #333;
                        }
                        .print-info-grid strong {
                            color: #1a1a2e;
                        }
                        .print-description {
                            padding: 0 15px 12px 15px;
                            font-size: 13px;
                            border-top: 1px solid #eee;
                            padding-top: 10px;
                            color: #444;
                        }
                        /* Table styles */
                        .print-table {
                            width: 100%;
                            border-collapse: collapse;
                            font-size: 11px;
                        }
                        .print-table th {
                            padding: 6px 8px;
                            border: 1px solid #ddd;
                            background: #f5f5f5;
                            text-align: center;
                            font-weight: 600;
                            color: #333;
                        }
                        .print-table td {
                            padding: 6px 8px;
                            border: 1px solid #ddd;
                            text-align: center;
                            color: #333;
                        }
                        .print-table td.left {
                            text-align: left;
                        }
                        .print-table td.bold {
                            font-weight: bold;
                        }
                        .print-table tfoot td {
                            font-weight: bold;
                            background: #f9f9f9;
                        }
                        .print-table input[type="checkbox"] {
                            width: 16px;
                            height: 16px;
                            accent-color: #27ae60;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .print-table .quoted-cell {
                            background-color: #e8f5e9 !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .print-table .registered-cell {
                            background-color: #fff3e0 !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .print-table .empty-cell {
                            background-color: #fafafa !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .quote-price {
                            font-size: 10px;
                            font-weight: bold;
                            color: #27ae60;
                        }
                        .status-approved {
                            color: #27ae60;
                            font-weight: bold;
                            font-size: 8px;
                        }
                        .status-selected {
                            color: #f39c12;
                            font-weight: bold;
                            font-size: 8px;
                        }
                        .legend {
                            padding: 10px 15px;
                            border-top: 1px solid #eee;
                            display: flex;
                            flex-wrap: wrap;
                            gap: 20px;
                            font-size: 11px;
                            color: #666;
                        }
                        .legend-item {
                            display: flex;
                            align-items: center;
                            gap: 6px;
                        }
                        .legend-color {
                            display: inline-block;
                            width: 14px;
                            height: 14px;
                            border: 1px solid #ddd;
                        }
                        .legend-color.quoted { background: #e8f5e9; border-color: #c8e6c9; }
                        .legend-color.registered { background: #fff3e0; border-color: #ffe0b2; }
                        .legend-color.empty { background: #fafafa; border-color: #eee; }
                        .print-footer {
                            text-align: center;
                            padding-top: 15px;
                            border-top: 1px solid #ddd;
                            margin-top: 15px;
                            font-size: 11px;
                            color: #888;
                        }
                        .print-footer p {
                            margin: 3px 0;
                        }
                        /* Status badge in table */
                        .status-badge {
                            padding: 2px 10px;
                            border-radius: 4px;
                            font-size: 10px;
                            font-weight: bold;
                            display: inline-block;
                        }
                        .status-badge.approved {
                            background: #e8f5e9;
                            color: #27ae60;
                        }
                        .status-badge.selected {
                            background: #fff3e0;
                            color: #f39c12;
                        }
                        .status-badge.rejected {
                            background: #ffebee;
                            color: #dc3545;
                        }
                        .status-badge.converted {
                            background: #e3f2fd;
                            color: #1976d2;
                        }
                        .status-badge.pending {
                            background: #f5f5f5;
                            color: #666;
                        }
                        /* Print-specific */
                        @page {
                            size: landscape;
                            margin: 10mm 12mm;
                        }
                        @media print {
                            body {
                                padding: 0;
                            }
                            .no-print {
                                display: none !important;
                            }
                            .print-section {
                                break-inside: avoid;
                                page-break-inside: avoid;
                            }
                            .print-table {
                                font-size: 10px !important;
                            }
                            .print-table th,
                            .print-table td {
                                padding: 4px 6px !important;
                            }
                        }
                        /* Responsive */
                        @media screen and (max-width: 768px) {
                            .print-info-grid {
                                grid-template-columns: 1fr;
                            }
                            .print-header-info {
                                flex-direction: column;
                                gap: 6px;
                                align-items: center;
                            }
                            .print-table {
                                font-size: 9px;
                            }
                            .print-table th,
                            .print-table td {
                                padding: 3px 4px;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="print-content">
                        ${contentHTML}
                    </div>
                    <script>
                        // Auto-print when loaded
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                            }, 500);
                        };
                    <\/script>
                </body>
            </html>
        `);

        printWindow.document.close();

        // Handle close
        const checkPrintDone = setInterval(() => {
            if (printWindow.closed) {
                clearInterval(checkPrintDone);
                setIsPrinting(false);
            }
        }, 1000);

        // Clean up after print dialog closes
        setTimeout(() => {
            if (!printWindow.closed) {
                // Don't force close - let user close naturally
            }
        }, 30000);
    };

    // Mark as ready when content is rendered
    useEffect(() => {
        setIsReady(true);
    }, []);

    // Auto-open print when component mounts and is ready
    useEffect(() => {
        if (isReady) {
            // Small delay to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                handlePrint();
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [isReady]);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    // Build the print content HTML
    const renderPrintContent = () => {
        if (!request) return null;

        return (
            <div ref={printRef}>
                {/* Header with Logo */}
                <div className="print-header">
                    <div className="logo-container">
                        <img
                            src={logo}
                            alt="Grupo Logo"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <div>
                            <h1>SOLICITAÇÃO DE COMPRA</h1>
                            <p className="subtitle">{request?.code || 'Código não disponível'}</p>
                        </div>
                    </div>
                    <div className="print-header-info">
                        <div><strong>Data:</strong> {request?.requestDate || new Date().toLocaleDateString('pt-BR')}</div>
                        <div><strong>Status:</strong> {getStatusLabel(request?.status)}</div>
                        <div><strong>Prioridade:</strong> {request?.priority || 'Média'}</div>
                        <div><strong>Solicitante:</strong> {request?.requestedBy || 'N/A'}</div>
                    </div>
                </div>

                {/* Request Info */}
                <div className="print-section">
                    <div className="print-section-title">
                        <span>Informações da Solicitação</span>
                        <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#666' }}>
                            Tipo: {request?.requestType || 'N/A'}
                        </span>
                    </div>
                    <div className="print-info-grid">
                        <div><strong>Código:</strong> {request?.code}</div>
                        <div><strong>Data:</strong> {request?.requestDate || 'N/A'}</div>
                        <div><strong>Data Necessária:</strong> {request?.requiredDate || 'N/A'}</div>
                        <div><strong>Departamento:</strong> {request?.department || 'N/A'}</div>
                        <div><strong>Solicitante:</strong> {request?.requestedBy || 'N/A'}</div>
                        <div><strong>Total:</strong> {formatCurrency(request?.totalValue || 0)}</div>
                    </div>
                    {request?.description && (
                        <div className="print-description">
                            <strong>Descrição:</strong> {request.description}
                        </div>
                    )}
                </div>

                {/* Items Table with Supplier Checkboxes */}
                <div className="print-section">
                    <div className="print-section-title">
                        <span>Itens da Solicitação</span>
                        <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#666' }}>
                            {request?.items?.length || 0} itens
                        </span>
                    </div>

                    <div style={{ padding: '10px 15px', overflowX: 'auto' }}>
                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '30px' }}>#</th>
                                    <th style={{ textAlign: 'left' }}>Código</th>
                                    <th style={{ textAlign: 'left' }}>Produto</th>
                                    <th style={{ width: '60px' }}>Qtde</th>
                                    <th style={{ width: '80px' }}>Preço Est.</th>
                                    <th style={{ width: '80px' }}>Total Est.</th>
                                    {allSuppliers.map(supplier => (
                                        <th key={supplier.id} style={{
                                            minWidth: '80px',
                                            fontSize: '9px',
                                            textAlign: 'center'
                                        }}>
                                            {supplier.codigo && <div style={{ fontWeight: 'normal', fontSize: '8px', color: '#888' }}>{supplier.codigo}</div>}
                                            <div style={{ fontWeight: 'bold', fontSize: '9px' }}>{supplier.razaoSocial?.substring(0, 15)}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {request?.items?.map((item, index) => {
                                    const itemTotal = parseFloat(item.total) || 0;
                                    const itemQuantity = parseFloat(item.quantity) || 0;
                                    const itemUnitPrice = parseFloat(item.unitPrice) || 0;

                                    return (
                                        <tr key={item.id || index}>
                                            <td>{index + 1}</td>
                                            <td className="left">{item.code || 'N/A'}</td>
                                            <td className="left">{item.name || 'N/A'}</td>
                                            <td>{itemQuantity}</td>
                                            <td>{itemUnitPrice ? formatCurrency(itemUnitPrice) : '-'}</td>
                                            <td className="bold">{itemTotal ? formatCurrency(itemTotal) : '-'}</td>
                                            {allSuppliers.map(supplier => {
                                                const isQuoted = isSupplierQuotedForItem(supplier.id, item.id);
                                                const quotePrice = getQuotePriceForItem(supplier.id, item.id);
                                                const quoteStatus = getSupplierQuoteStatus(supplier.id);
                                                const isRegistered = (item.suppliers || []).some(s => s.supplierId === supplier.id);

                                                let cellClass = 'empty-cell';
                                                if (isQuoted) cellClass = 'quoted-cell';
                                                else if (isRegistered) cellClass = 'registered-cell';

                                                return (
                                                    <td key={supplier.id} className={cellClass}>
                                                        {isRegistered ? (
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isQuoted}
                                                                    readOnly
                                                                />
                                                                {isQuoted && quotePrice !== null && (
                                                                    <span className="quote-price">{formatCurrency(quotePrice)}</span>
                                                                )}
                                                                {quoteStatus === 'approved' && (
                                                                    <span className="status-approved">✓ Aprovado</span>
                                                                )}
                                                                {quoteStatus === 'selected' && (
                                                                    <span className="status-selected">Selecionado</span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span style={{ fontSize: '10px', color: '#ccc' }}>—</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'right' }}>Total Geral:</td>
                                    <td className="bold">{formatCurrency(request?.totalValue || 0)}</td>
                                    <td colSpan={allSuppliers.length}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Legend */}
                    <div className="legend">
                        <div className="legend-item">
                            <span className="legend-color quoted"></span>
                            Cotado
                        </div>
                        <div className="legend-item">
                            <span className="legend-color registered"></span>
                            Registrado (não cotado)
                        </div>
                        <div className="legend-item">
                            <span className="legend-color empty"></span>
                            Não registrado
                        </div>
                        <div className="legend-item">
                            <span style={{ color: '#27ae60', fontWeight: 'bold' }}>✓ Aprovado</span>
                        </div>
                        <div className="legend-item">
                            <span style={{ color: '#f39c12', fontWeight: 'bold' }}>Selecionado</span>
                        </div>
                    </div>
                </div>

                {/* Supplier Quotes Summary */}
                {request?.supplierQuotes && request.supplierQuotes.length > 0 && (
                    <div className="print-section">
                        <div className="print-section-title">
                            <span>Resumo das Cotações por Fornecedor</span>
                            <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#666' }}>
                                {request.supplierQuotes.length} cotações
                            </span>
                        </div>
                        <div style={{ padding: '10px 15px' }}>
                            <table className="print-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Fornecedor</th>
                                        <th>Itens</th>
                                        <th>Valor Total</th>
                                        <th>Prazo (dias)</th>
                                        <th>Pagamento</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.supplierQuotes.map((quote, idx) => (
                                        <tr key={idx}>
                                            <td className="left">
                                                {getSupplierName(quote.supplierId)}
                                                {quote.supplierCode && <span style={{ color: '#888', fontSize: '10px', marginLeft: '4px' }}>({quote.supplierCode})</span>}
                                            </td>
                                            <td>{quote.items?.length || 0}</td>
                                            <td className="bold">{formatCurrency(quote.totalValue || 0)}</td>
                                            <td>{quote.deliveryTime || '-'}</td>
                                            <td>{quote.paymentTerms || '-'}</td>
                                            <td>
                                                <span className={`status-badge ${quote.status || 'pending'}`}>
                                                    {quote.status === 'approved' ? 'Aprovado' :
                                                        quote.status === 'selected' ? 'Selecionado' :
                                                            quote.status === 'rejected' ? 'Rejeitado' :
                                                                quote.status === 'converted' ? 'Convertido' :
                                                                    quote.status === 'pending' ? 'Pendente' : quote.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Tracking History */}
                {request?.trackingHistory && request.trackingHistory.length > 0 && (
                    <div className="print-section">
                        <div className="print-section-title">
                            <span>Histórico de Alterações</span>
                            <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#666' }}>
                                {request.trackingHistory.length} registros
                            </span>
                        </div>
                        <div style={{ padding: '10px 15px' }}>
                            <table className="print-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Data</th>
                                        <th style={{ textAlign: 'left' }}>Status</th>
                                        <th style={{ textAlign: 'left' }}>Descrição</th>
                                        <th style={{ textAlign: 'left' }}>Responsável</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.trackingHistory.map((entry, idx) => (
                                        <tr key={idx}>
                                            <td className="left">{entry.date || 'N/A'}</td>
                                            <td className="left"><strong>{entry.status || 'N/A'}</strong></td>
                                            <td className="left">{entry.description || '-'}</td>
                                            <td className="left">{entry.updatedBy || 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="print-footer">
                    <p>Documento gerado em {new Date().toLocaleString('pt-BR')}</p>
                    <p style={{ fontSize: '10px', color: '#aaa' }}>
                        {request?.code} - Solicitação de Compra
                    </p>
                </div>
            </div>
        );
    };

    return ReactDOM.createPortal(
        <div className={styles.modalBackdrop} style={{ zIndex: 10001 }}>
            <div className={`${styles.formContainer} ${styles.printModalContainer}`} style={{ maxWidth: '1200px' }}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2>Imprimir Solicitação</h2>
                        <p className={styles.modalSubtitle}>
                            {request?.code} - {request?.title || 'Sem título'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        disabled={isPrinting}
                    >
                        <span style={{ fontSize: '20px' }}>×</span>
                    </button>
                </div>

                <div className={styles.formContent} style={{ padding: '20px', background: '#fff' }}>
                    {renderPrintContent()}
                </div>

                <div className={styles.stepperActions} style={{ borderTop: '1px solid #e0e0e0' }}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.secondaryButton}
                        disabled={isPrinting}
                    >
                        Fechar
                    </button>
                    <button
                        type="button"
                        onClick={handlePrint}
                        className={styles.primaryButton}
                        disabled={isPrinting}
                    >
                        {isPrinting ? 'Preparando...' : '🖨️ Imprimir / PDF'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PrintModal;